/**
 * Campaign form data contract (SalesManager AI) -- frontend <-> backend.
 *
 * Each form section has a matching `createEmptyXxx()` factory, mirroring
 * models/jobOffer/jobOfferModel.js. `createEmptyCampaign()` composes all
 * sections into one object used as Formik initialValues.
 */

// ---------------------------------------------------------------------------
// Products (Krok: Produkty i usługi)
// ---------------------------------------------------------------------------

export const PRODUCT_TYPE_OPTIONS = [
    { value: "PhysicalProduct", label: "Produkt fizyczny" },
    { value: "Service", label: "Usługa" },
    { value: "Subscription", label: "Subskrypcja" },
    { value: "Other", label: "Inny" },
];

export const BILLING_CYCLE_OPTIONS = [
    { value: "Monthly", label: "Miesięcznie" },
    { value: "Yearly", label: "Rocznie" },
    { value: "Custom", label: "Inny okres" },
];

export const createEmptyProductPrice = () => ({
    price: null, currency: "PLN", priceFrom: null, priceTo: null, pricingMethod: "",
    subscription: false, oneTimeFee: null, implementationCost: null,
    additionalCosts: [], discounts: [], promoConditions: "", billingCycle: null,
});

export const createEmptyProductAvailability = () => ({
    isAvailable: true, leadTime: "", deliveryTime: "", localAvailability: "",
    limitedQuantity: false, serviceStartDate: null,
});

export const createEmptyProductSafetyInfo = () => ({
    contraindications: [], warnings: [], hazards: [], requiredProtection: [],
    ageRestrictions: "", usageRestrictions: [],
});

/**
 * @typedef {Object} CampaignProduct
 * @property {string} id
 * @property {string} name
 * @property {string|null} productType - enum ProductType, drives which sections the form shows
 * @property {boolean} showAllFields - UI-only escape hatch for productType "Other"
 */
export const createEmptyProduct = () => ({
    id: crypto.randomUUID(), name: "", productType: null, category: "",
    shortDescription: "", fullDescription: "",
    manufacturer: "", brand: "", model: "", variant: "",
    applications: [], benefits: [], usp: [], parameters: {},
    price: createEmptyProductPrice(),
    availability: createEmptyProductAvailability(),
    usageInstructions: "",
    safetyInfo: createEmptyProductSafetyInfo(),
    attachments: [], faq: [],
    // UI-only: productType "Other" defaults to core fields only, this reveals the rest.
    showAllFields: false,
});

// ---------------------------------------------------------------------------
// Company knowledge (Krok: Wiedza o firmie)
// ---------------------------------------------------------------------------

export const createEmptyCompanyInfo = () => ({
    companyName: "", brand: "", website: "", shortDescription: "", whatWeDo: "", whyChooseUs: "",
    keyAdvantages: [], experience: "", references: [], certificates: [], awards: [], partners: [],
    serviceArea: "", locations: [], operatingHours: "",
});

// ---------------------------------------------------------------------------
// Sales & conversion (Krok: Sprzedaż i konwersja)
// ---------------------------------------------------------------------------

export const createEmptyObjection = () => ({
    objection: "", responseStyle: "", argument: "", alternativeOffer: "", allowDiscount: false, followUpQuestion: "",
});

export const createEmptyConversionGoal = () => ({
    goalType: "Meeting", link: "", description: "", preferredCta: [],
    calendarSystem: null, calendarLink: null,
});

export const createEmptyEscalationConfig = () => ({
    conditions: [], transactionValueThreshold: null, handoffSummaryTemplate: "",
});

// ---------------------------------------------------------------------------
// Qualifying (Krok: Kwalifikacja)
// ---------------------------------------------------------------------------

export const createEmptyQualifyingQuestion = () => ({
    id: crypto.randomUUID(), question: "", answerType: "Text", isRequired: false, isCritical: false,
    options: [], conditionQuestionId: null, conditionValue: "",
});

// ---------------------------------------------------------------------------
// Lead source (Krok: Źródło leadów)
// ---------------------------------------------------------------------------

export const createEmptyLeadSourceConfig = () => ({
    spreadsheetUrl: "", sheetName: "", isConnected: false, decisionColumn: "", decisionColumnAllowedValues: [],
    statusColumn: "", statusValueMapping: [], defaultStatus: "",
    excelFileUrl: "", excelWorksheetName: "", excelConnectionId: null,
});

export const createEmptyCustomField = () => ({ fieldName: "", sourceColumn: "" });

// ---------------------------------------------------------------------------
// Automation: status -> channel -> follow-up (Krok: Automatyzacja)
// ---------------------------------------------------------------------------

export const createEmptyStatusRule = () => ({
    id: crypto.randomUUID(), status: "New", isActive: false, agentAction: "",
    primaryChannel: null, fallbackChannel: null,
    channelSequenceMode: "Sequential", delayMinutes: null, parallelCommunication: false,
    startMessageTemplate: "",
});

export const createEmptyCommunicationChannels = () => ({
    channelOrder: ["WhatsApp", "Sms", "Email"],
    delayBetweenChannelsMinutes: null,
    parallelCommunication: false,
    whatsApp: { enabled: false, senderNumber: "", fallbackChannel: null, startMessageTemplate: "", retryRules: "" },
    sms: { enabled: false, senderIdOrNumber: "", fallbackChannel: null, messageTemplate: "" },
    email: { enabled: false, senderAddress: "", senderName: "", subject: "", bodyTemplate: "", footer: "", webAgentLink: "", allowAttachments: false },
});

export const createEmptyFollowUpAttempt = () => ({ id: crypto.randomUUID(), delayMinutes: 0, channel: null, messageTemplate: "" });

export const createEmptyFollowUpConfig = () => ({
    attempts: [],
    stopConditions: ["LeadResponded", "LeadConverted", "MeetingBooked", "StatusChangedManually", "LeadRequestedNoContact"],
});

// ---------------------------------------------------------------------------
// Conversation style / agent identity (Krok: Kampania i tożsamość Agenta)
// ---------------------------------------------------------------------------

export const createEmptyConversationStyle = () => ({
    agentName: "", introductionTemplate: "", brandStyle: "",
    addressForm: null, useEmoji: false, messageLength: null, maxQuestionsPerMessage: null,
    questionStyle: "", inConversationFollowUpRules: "", closingStyle: "",
    salesInitiativeLevel: null, escalationRules: "",
    forbiddenAssumptions: ["Nie wymyślaj cen", "Nie obiecuj terminów", "Nie wymyślaj rabatów"],
});

// ---------------------------------------------------------------------------
// Output / integracje (Krok: Output i test)
// ---------------------------------------------------------------------------

export const createEmptyOutputIntegration = () => ({
    id: crypto.randomUUID(), targetSystem: "GoogleSheets", trigger: "AfterStatusChange",
    action: "UpdateExistingRecord", isActive: true, updateSourceSheet: false, isConnected: false,
    spreadsheetUrl: "", sheetName: "", excelFileUrl: "", excelWorksheetName: "", excelConnectionId: null,
    keyTargetColumn: "Lead ID", fieldMapping: [],
});

// ---------------------------------------------------------------------------
// Full form -- Campaign (contract sent to the backend, see campaignMapper.js)
// ---------------------------------------------------------------------------

export const createEmptyCampaign = () => ({
    id: null,
    name: "",
    agentDisplayName: "",
    description: "",
    languages: [],
    companyInfo: createEmptyCompanyInfo(),
    products: [],
    salesArguments: [],
    objections: [],
    conversionGoals: [],
    sendNotifications: false,
    notificationEmails: [],
    leadSourceType: null,
    scoringOverrides: [],
    localCriteria: [],
    qualifyingQuestions: [],
    qualificationCriteria: [],
    disqualificationAction: null,
    disqualificationActionDetail: "",
    escalationConfig: createEmptyEscalationConfig(),
    leadSourceConfig: createEmptyLeadSourceConfig(),
    leadFieldMappings: [],
    customFieldMappings: [],
    statusAutomationRules: [],
    communicationChannels: createEmptyCommunicationChannels(),
    followUpConfig: createEmptyFollowUpConfig(),
    outputIntegrations: [],
    conversationStyle: createEmptyConversationStyle(),
    status: "Draft",
});
